import React, { useContext } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { mq } from '@stylesheets/tools';

import { Context } from '../../../context/provider';
import IntroCard from './IntroCard';
import ErrorCard from './ErrorCard';
import ContentCard from './ContentCard';

export const StyledCardStack = styled.div`
  position: relative;
  width: 100%;

  .card-stack__wrapper {
    position: relative;
    aspect-ratio: 3 / 4;
    margin-right: 11%;
    margin-top: 11%;
    aspect-ratio: 4 / 5;
    max-width: 320px;

    ${mq('s')} {
      aspect-ratio: 7 / 8;
    }

    ${mq('m')} {
      max-width: 520px;
      margin: 7% auto 0;
      aspect-ratio: 4 / 3;
    }

    ${mq('l')} {
      margin: 9% 0 0;
    }

    ${mq('xxl')} {
      margin: 16% 0 0;
      max-width: 580px;
    }
  }

  .card-stack__card {
    position: absolute;
    width: 100%;
  }
`;

const CardStack = ({ introCard, className, ...rest }) => {
  const { getQueryParams, activeIndex, isFiltered } = useContext(Context);
  const { hits } = useInfiniteHits();

  const CARD_OFFSET = typeof window !== 'undefined' && window.innerWidth < 768 ? 20 : 26;
  const CARD_AMOUNT = 3;
  const SCALE_STEP = 0.035;

  const addEllipsisToIntroCopy = (introCopy, headline) => {
    const HEADLINE_MAX_ONE_ROW_CHARACTERS = 39;
    const INTRO_COPY_MAX_CHARACTERS_LARGE = 368;
    const INTRO_COPY_MAX_CHARACTERS_SMALL = 303;
    const isHeadlineOneRow = headline.length < HEADLINE_MAX_ONE_ROW_CHARACTERS;

    // Remove tags from content
    const div = document.createElement('div');
    div.innerHTML = introCopy;
    const text = div.textContent || div.innerText || '';

    let introCopyFormatted =
      text.length > INTRO_COPY_MAX_CHARACTERS_SMALL
        ? `${text.substring(0, INTRO_COPY_MAX_CHARACTERS_SMALL)}...`
        : text;

    if (isHeadlineOneRow) {
      introCopyFormatted =
        text.length > INTRO_COPY_MAX_CHARACTERS_LARGE
          ? `${text.substring(0, INTRO_COPY_MAX_CHARACTERS_LARGE)}...`
          : text;
    }

    return introCopyFormatted;
  };

  return (
    <StyledCardStack className={className}>
      <div className="card-stack__wrapper">
        {hits.length === 0 && <ErrorCard />}

        {hits.length > 0 && introCard && !isFiltered && (
          <motion.div
            animate={{
              x: activeIndex * -CARD_OFFSET,
              y: activeIndex * CARD_OFFSET,
              z: activeIndex * CARD_OFFSET,
              zIndex: (hits?.length || 0) + 1,
              pointerEvents: activeIndex !== 0 ? 'none' : 'auto',
              opacity: activeIndex === 0 ? 1 : 0
            }}
            initial={{
              x: activeIndex * -CARD_OFFSET,
              y: activeIndex * CARD_OFFSET,
              z: activeIndex * CARD_OFFSET,
              opacity: 1
            }}
            transition={{
              ease: 'linear',
              duration: 0.4
            }}
            className="card-stack__card"
          >
            <IntroCard
              headline={introCard.headline}
              subline={introCard.headlineSmall}
              copy={introCard.copy}
            />
          </motion.div>
        )}

        {hits.map((hit, index) => {
          const realActiveIndex = activeIndex - 1;
          const isActive = realActiveIndex === index;
          const isHidden = realActiveIndex + CARD_AMOUNT < index;

          let offset = isActive ? 0 : (realActiveIndex - index) * CARD_OFFSET;
          const isScrolledBy = Math.sign(offset) === 1;

          if (isHidden) offset = CARD_AMOUNT * CARD_OFFSET * -1;
          if (isScrolledBy) offset = CARD_OFFSET;

          const opacity = isScrolledBy || isHidden ? 0 : 1;
          const x = offset * -1;
          const y = isHidden || isScrolledBy ? offset + 20 : offset;
          const scale = 1 - Math.abs(realActiveIndex - index) * SCALE_STEP;

          return (
            <motion.div
              key={hit.objectID}
              animate={{
                x,
                y,
                scale,
                zIndex: (hits?.length || 0) - index,
                pointerEvents: !isActive ? 'none' : 'auto',
                opacity
              }}
              transition={{
                ease: 'linear',
                duration: 0.4,
                opacity: {
                  duration: 0.2
                }
              }}
              initial={{
                x,
                y,
                scale,
                opacity
              }}
              exit={{
                opacity: 0,
                transition: { duration: 0, delay: 0 }
              }}
              className="card-stack__card"
            >
              <ContentCard
                image={hit.image}
                href={`${hit.uri}?${getQueryParams()}`}
                headline={hit.headline ?? hit.title}
                copy={addEllipsisToIntroCopy(hit.introCopy, hit.headline ?? hit.title)}
              />
            </motion.div>
          );
        })}
      </div>
    </StyledCardStack>
  );
};

CardStack.propTypes = {
  hits: PropTypes.shape().isRequired,
  className: PropTypes.string,
  introCard: PropTypes.shape()
};

export default CardStack;
